// Dictée vocale (Web Speech API) pour saisir au chantier sans clavier : désignation d'une ligne, note de journal.
// Le navigateur envoie l'audio à son service de reconnaissance vocale (Google sur Chrome et Android, Apple sur
// iPhone) : ce n'est pas un traitement local. L'utilisateur le confirme une fois avant le premier usage.

type EvenementResultat = { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }
type Reconnaissance = {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((e: EvenementResultat) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
}
type Constructeur = new () => Reconnaissance

const constructeur = (): Constructeur | undefined => {
  if (typeof window === 'undefined') return undefined
  const w = window as unknown as { SpeechRecognition?: Constructeur; webkitSpeechRecognition?: Constructeur }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition
}

export const dicteeDisponible = () => !!constructeur()

const CLE_ACCORD = 'filaplomb.dictee.accord'
export const MESSAGE_ACCORD =
  'La dictée utilise le service de reconnaissance vocale de votre navigateur (Google sur Chrome et Android, Apple sur iPhone) : ce que vous dites lui est envoyé pour être transcrit. Continuer ?'

export function accordDonne(): boolean {
  try {
    return localStorage.getItem(CLE_ACCORD) === '1'
  } catch {
    return false
  }
}
export function memoriserAccord() {
  try {
    localStorage.setItem(CLE_ACCORD, '1')
  } catch {
    // stockage indisponible : l'accord sera redemandé
  }
}

/**
 * Ajoute un texte dicté à un texte existant : une espace entre les deux, une majuscule après un point ou un début de
 * texte. La reconnaissance rend du texte sans majuscule initiale en dictée continue.
 */
export function assembler(existant: string, dicte: string): string {
  const d = dicte.trim()
  if (!d) return existant
  const base = existant.trimEnd()
  const majuscule = base === '' || /[.!?…]$/.test(base)
  const mot = majuscule ? d.charAt(0).toLocaleUpperCase('fr-FR') + d.slice(1) : d
  return base === '' ? mot : `${base} ${mot}`
}

export type SessionDictee = { arreter: () => void }

/**
 * Lance l'écoute. `surPhrase` reçoit chaque phrase définitive ; `surFin` est appelé à l'arrêt (demandé ou non),
 * avec la raison d'une erreur éventuelle (`not-allowed` : micro refusé, `network`, `no-speech`…).
 */
export function demarrerDictee(options: { surPhrase: (texte: string) => void; surFin: (erreur?: string) => void; langue?: string }): SessionDictee | null {
  const C = constructeur()
  if (!C) return null
  const r = new C()
  r.lang = options.langue ?? 'fr-FR'
  r.continuous = true
  r.interimResults = false
  let erreur: string | undefined
  r.onresult = (e) => {
    for (let i = e.resultIndex; i < e.results.length; i++) {
      const resultat = e.results[i]!
      if (resultat.isFinal && resultat[0]) options.surPhrase(resultat[0].transcript)
    }
  }
  r.onerror = (e) => {
    erreur = e.error === 'no-speech' || e.error === 'aborted' ? undefined : e.error
  }
  r.onend = () => options.surFin(erreur)
  r.start()
  return { arreter: () => r.stop() }
}

export const MESSAGES_ERREUR_DICTEE: Record<string, string> = {
  'not-allowed': 'Le micro est refusé : autorisez-le dans les réglages du navigateur.',
  'service-not-allowed': 'La reconnaissance vocale est refusée sur cet appareil.',
  network: 'La dictée demande du réseau (le service de transcription est en ligne).',
  'audio-capture': 'Aucun micro n’a été trouvé.',
  'language-not-supported': 'Le français n’est pas pris en charge par la dictée de ce navigateur.',
}
